import { Link } from 'react-router-dom'
import type { MedicationPrescription } from '../../store/medicationsSlice'

interface MedicationPrescriptionsProps {
  prescriptions: MedicationPrescription[]
}

function formatRxNumber(num: number): string {
  return `RX-${String(num).padStart(4, '0')}`
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'Active':
      return 'bg-accent-subtle-bg text-status-success'
    case 'Completed':
      return 'bg-border text-text-muted'
    case 'Discontinued':
      return 'bg-[#f0dede] text-status-error'
    case 'Expired':
      return 'bg-[#f0e8d0] text-status-warning'
    default:
      return 'bg-border text-text-muted'
  }
}

export function MedicationPrescriptions({ prescriptions }: MedicationPrescriptionsProps) {
  return (
    <div data-testid="medication-prescriptions-section" className="bg-surface border border-border rounded-[6px]">
      <div className="border-b border-border-light px-4 py-2.5">
        <h2 className="text-[14px] font-medium text-text-primary">Prescriptions Using This Medication</h2>
      </div>

      <div className="px-4 py-3">
        {prescriptions.length === 0 ? (
          <p className="text-[13px] text-text-disabled italic" data-testid="medication-prescriptions-empty">
            No active prescriptions for this medication
          </p>
        ) : (
          <div data-testid="medication-prescriptions-list" className="border border-border rounded-[4px]">
            <div className="mp-table-header grid grid-cols-[100px_1fr_1fr_80px] gap-2 px-3 py-2 border-b border-border-light text-[11px] text-text-muted font-medium">
              <span>Rx Number</span>
              <span>Patient</span>
              <span>Dosage</span>
              <span>Status</span>
            </div>
            {prescriptions.map((rx) => (
              <Link
                key={rx.id}
                to={`/prescriptions/${rx.id}`}
                data-testid="medication-prescription-row"
                className="mp-table-row grid grid-cols-[100px_1fr_1fr_80px] gap-2 px-3 py-2 border-b border-border-light last:border-b-0 text-[13px] text-text-primary hover:bg-hover transition-colors duration-100 no-underline"
              >
                <span className="text-text-muted font-medium">{formatRxNumber(rx.prescription_number)}</span>
                <span>
                  <Link
                    to={`/patients/${rx.patient_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-accent hover:underline"
                    data-testid="prescription-patient-link"
                  >
                    {rx.patient_name}
                  </Link>
                </span>
                <span className="text-text-muted">
                  {rx.dosage} {rx.frequency}
                </span>
                <span>
                  <span className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${statusBadgeClass(rx.status)}`}>
                    {rx.status}
                  </span>
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
